/**
 * Navigation handling utilities for step/substep transitions
 */
import { getStepId, getSubstepId } from './getters';
import {
  findFirstIncompleteSubstep,
  findFirstRealSubstep,
  findStepForSubstep,
  findSubstepById
} from './stepFinders';
import { buildNavigationUrl, urlNeedsUpdate } from './urlUtils';

/**
 * Handles navigation when no step_id is present in URL
 * Navigates to the first non-special substep
 * @param {Object} options - Navigation options
 * @param {Array} options.flattenedSubsteps - Flattened array of substeps
 * @param {Array} options.stepsState - Array of steps
 * @param {string} options.baseUrl - Base URL path
 * @param {Function} options.navigate - React Router navigate function
 * @returns {boolean} True if navigation was triggered
 */
export const handleMissingStepId = ({ flattenedSubsteps, stepsState, baseUrl, navigate }) => {
  const firstRealSubstep = findFirstRealSubstep(flattenedSubsteps, stepsState);

  if (!firstRealSubstep) return false;

  const stepForSubstep = findStepForSubstep(stepsState, firstRealSubstep.code);
  if (!stepForSubstep) return false;

  const stepId = getStepId(stepForSubstep);
  const substepId = getSubstepId(firstRealSubstep);
  const hasSubsteps = stepForSubstep.substeps?.length > 0;

  const url = hasSubsteps
    ? buildNavigationUrl(baseUrl, stepId, substepId)
    : buildNavigationUrl(baseUrl, stepId);

  navigate(url, { replace: true });
  return true;
};

/**
 * Handles navigation when step_id is invalid
 * Navigates to the first available substep
 * @param {Object} options - Navigation options
 * @param {Array} options.flattenedSubsteps - Flattened array of substeps
 * @param {Array} options.stepsState - Array of steps
 * @param {string} options.baseUrl - Base URL path
 * @param {Function} options.navigate - React Router navigate function
 * @returns {boolean} True if navigation was triggered
 */
export const handleInvalidStepId = ({ flattenedSubsteps, stepsState, baseUrl, navigate }) => {
  const firstSubstep = flattenedSubsteps[0];
  if (!firstSubstep) return false;

  const stepForSubstep = findStepForSubstep(stepsState, firstSubstep.code);
  if (!stepForSubstep) return false;

  const stepId = getStepId(stepForSubstep);
  const substepId = getSubstepId(firstSubstep);
  const hasSubsteps = stepForSubstep.substeps?.length > 0;

  const url = hasSubsteps
    ? buildNavigationUrl(baseUrl, stepId, substepId)
    : buildNavigationUrl(baseUrl, stepId);

  navigate(url, { replace: true });
  return true;
};

/**
 * Navigates to the first incomplete (non-completed, non-special) step.
 * Falls back to the first real substep if all steps are completed.
 * @param {Object} options - Navigation options
 * @param {Array} options.flattenedSubsteps - Flattened array of substeps
 * @param {Array} options.stepsState - Array of steps
 * @param {string} options.baseUrl - Base URL path
 * @param {Function} options.navigate - React Router navigate function
 * @returns {boolean} True if navigation was triggered
 */
export const handleFirstIncompleteStep = ({ flattenedSubsteps, stepsState, baseUrl, navigate }) => {
  const incompleteSubstep = findFirstIncompleteSubstep(flattenedSubsteps, stepsState);
  const targetSubstep = incompleteSubstep || findFirstRealSubstep(flattenedSubsteps, stepsState);

  if (!targetSubstep) return false;

  const stepForSubstep = findStepForSubstep(stepsState, targetSubstep.code);
  if (!stepForSubstep) return false;

  const stepId = getStepId(stepForSubstep);
  const substepId = getSubstepId(targetSubstep);
  const hasSubsteps = stepForSubstep.substeps?.length > 0;

  const url = hasSubsteps
    ? buildNavigationUrl(baseUrl, stepId, substepId)
    : buildNavigationUrl(baseUrl, stepId);

  navigate(url, { replace: true });
  return true;
};

/**
 * Handles navigation for a step that has substeps
 * @param {Object} options - Navigation options
 * @param {Object} options.targetStep - Target step object
 * @param {string} options.stepId - Step ID from URL
 * @param {string} options.substepId - Substep ID from URL
 * @param {string} options.baseUrl - Base URL path
 * @param {Function} options.navigate - React Router navigate function
 * @param {Function} options.navigateToSubstep - Function to navigate to substep in state
 * @param {Object} options.isNavigatingFromUrl - Ref to track URL navigation
 * @returns {boolean} True if navigation was handled
 */
export const handleStepWithSubsteps = ({
  targetStep,
  stepId,
  substepId,
  baseUrl,
  navigate,
  navigateToSubstep,
  isNavigatingFromUrl
}) => {
  // No substep_id provided, navigate to first substep of this step
  if (!substepId) {
    const firstSubstep = targetStep.substeps[0];
    const firstSubstepId = getSubstepId(firstSubstep);
    navigate(buildNavigationUrl(baseUrl, stepId, firstSubstepId), { replace: true });
    return true;
  }

  const targetSubstep = findSubstepById(targetStep, substepId);

  // Invalid substep_id, navigate to first substep of this step
  if (!targetSubstep) {
    const firstSubstep = targetStep.substeps[0];
    const firstSubstepId = getSubstepId(firstSubstep);
    navigate(buildNavigationUrl(baseUrl, stepId, firstSubstepId), { replace: true });
    return true;
  }

  // Activate the substep if not already active
  if (!targetSubstep.active) {
    isNavigatingFromUrl.current = true;
    navigateToSubstep(targetSubstep.code, { fromUrl: true });
  }

  return true;
};

/**
 * Handles navigation for a step without substeps
 * @param {Object} options - Navigation options
 * @param {Object} options.targetStep - Target step object
 * @param {string} options.stepId - Step ID from URL
 * @param {string} options.substepId - Substep ID from URL (should be empty)
 * @param {string} options.baseUrl - Base URL path
 * @param {Function} options.navigate - React Router navigate function
 * @param {Function} options.activateStep - Function to activate step in state
 * @param {Object} options.isNavigatingFromUrl - Ref to track URL navigation
 * @returns {boolean} True if navigation was handled
 */
export const handleStepWithoutSubsteps = ({
  targetStep,
  stepId,
  substepId,
  baseUrl,
  navigate,
  activateStep,
  isNavigatingFromUrl
}) => {
  // Remove substep_id from URL if present
  if (substepId) {
    navigate(buildNavigationUrl(baseUrl, stepId), { replace: true });
    return true;
  }

  // Activate the step if not already active
  if (!targetStep.active) {
    isNavigatingFromUrl.current = true;
    activateStep(targetStep.code, { fromUrl: true });
  }

  return true;
};

/**
 * Syncs the URL based on current active step/substep state
 * @param {Object} options - Sync options
 * @param {Object} options.activeStep - Currently active step
 * @param {Object} options.activeSubstep - Currently active substep
 * @param {Object} options.params - URL params
 * @param {string} options.baseUrl - Base URL path
 * @param {Function} options.navigate - React Router navigate function
 */
export const syncUrlFromState = ({ activeStep, activeSubstep, params, baseUrl, navigate }) => {
  if (!activeStep) return;

  const stepId = getStepId(activeStep);
  const hasSubsteps = activeStep.substeps?.length > 0;

  if (hasSubsteps && activeSubstep) {
    const substepId = getSubstepId(activeSubstep);

    if (urlNeedsUpdate(params, stepId, substepId)) {
      navigate(buildNavigationUrl(baseUrl, stepId, substepId), { replace: true });
    }
  } else {
    if (urlNeedsUpdate(params, stepId)) {
      navigate(buildNavigationUrl(baseUrl, stepId), { replace: true });
    }
  }
};
